import { Component, MouseEvent, ReactNode } from 'react'
import * as clamp from 'lodash/clamp'
import * as debounce from 'lodash/debounce'
import * as cn from 'classnames'
import * as styles from 'styles/atoms/inputs/OldSelect.css'
import localStyles from 'components/features/pfm/DropdownItems/DropdownItems.styl'
import { getClassSelector } from 'helpers/styleHelpers'
import Scrollable from 'components/atoms/Scrollable'
import { Option } from 'interfaces/component'
import { QAMarked } from 'interfaces/component'
import { IS_FIREFOX } from 'helpers/userAgentHelpers'
import { scheduleCallOnTransitionEnd } from 'helpers/DOMHelpers'

const DROPDOWN_ITEMS_ANIMATION_TIMEOUT = 260 // slightly bigger then actual speed

type RenderContent = (content?: ReactNode) => ReactNode
type RenderOption<TValue> = (option: Option<TValue>, idx: number, optionRendered?: ReactNode) => ReactNode

export type DropdownContentRendererOptions<TValue> = {
  options: Option<TValue>[]
  renderContentWrapper: RenderContent
  renderOptionWrapper: RenderOption<TValue>
}

export type DroprownContentRenderer<TValue> = (arg: DropdownContentRendererOptions<TValue>) => ReactNode

type DropdownItemsProps<TValue> = QAMarked & {
  options: Option<TValue>[]
  selected: null | Option<TValue>
  isShown: boolean
  darkTheme?: boolean
  withBorder?: boolean
  isAlwaysBelowInput?: boolean

  allowOuterScroll?: boolean

  searchText?: string
  onSelect: (option: Option<TValue>) => void
  contentRenderer?: DroprownContentRenderer<TValue>
  footer?: ReactNode
}

type DropdownItemsState<TValue> = {
  isAboveInput: boolean
  isKeyPressed: boolean
  highlightedOption: null | Option<TValue>
  that: DropdownItems<TValue>
}

class DropdownItems<TValue> extends Component<DropdownItemsProps<TValue>, DropdownItemsState<TValue>> {
  static defaultProps: Partial<DropdownItemsProps<void>> = {
    darkTheme: false,
    withBorder: true,
  }

  static getDerivedStateFromProps (nextProps: DropdownItemsProps<any>, prevState: DropdownItemsState<any>) {
    const { that } = prevState
    const { isShown: nextShown, searchText: nextSearchText, selected } = nextProps
    const { isShown: prevShown, searchText: prevSearchText } = that.props

    let statePatch = {}
    const isVisibilityChanged = nextShown !== prevShown
    if (isVisibilityChanged) {
      // dropdown was hidden, do not remember highlighted
      if (!nextShown) {
        statePatch = { highlightedOption: null }
      }

      if (nextShown && selected) {
        statePatch = { highlightedOption: selected }
      }
    }

    if (nextSearchText && nextSearchText !== prevSearchText) {
      that.handleSearch(nextSearchText)
    }

    return statePatch
  }

  state: Readonly<DropdownItemsState<TValue>> = {
    isAboveInput: false,
    isKeyPressed: false,
    highlightedOption: null,
    that: this,
  }

  private dropdownNode: HTMLDivElement | null = null
  private scrollableNode: Scrollable | null = null

  // counter for preventing mouse events caused by this.adjustScroll
  private isScrollAdjustingCounter = 0

  render () {
    const { isAboveInput } = this.state
    const {
      darkTheme,
      isShown,
      contentRenderer,
      options,
      withBorder,
    } = this.props

    const dropdownClassName = cn(styles.dropdownItems, {
      [styles.isDropdownShown]: isShown,
      [styles.dark]: darkTheme,
      [styles.isAboveInput]: isAboveInput,
      [styles.withBorder]: withBorder,
    }, localStyles.dropdownItems, localStyles.classA, localStyles.classB)

    const contentChild = contentRenderer
      ? contentRenderer({
        renderContentWrapper: this.renderContent,
        renderOptionWrapper: this.renderOption,
        options,
      })
      : this.renderContent()

    // onClick works after blur so we should use onMouseDown
    return (
      <div
        ref={ (node: HTMLDivElement) => this.dropdownNode = node }
        className={ dropdownClassName }
        onMouseDown={ this.handleMouseDown }
      >
        { contentChild }
      </div>
    )
  }

  renderContent: RenderContent = (content) => {
    const { options, allowOuterScroll, qaMarker } = this.props
    const itemsClassName = cn(styles.items, {
      [styles.isFirefox]: IS_FIREFOX,
    }, localStyles.items)

    return options.length > 0 ? (
      <Scrollable
        ref={ (node) => this.scrollableNode = node }
        contentClassName={ itemsClassName }
        alwaysShowScrollbar
        allowOuterScroll={ allowOuterScroll }
        qaMarker={ qaMarker }
      >
        {
          content
            ? content
            : options.map((option, idx) => this.renderOption(option, idx))
        }
      </Scrollable>
    ) : null
  }

  renderOption: RenderOption<TValue> = (option, idx, optionRendered) => {
    const { highlightedOption } = this.state
    const { onSelect } = this.props

    const className = cn(styles.item, {
      [styles.itemHighlighted]: highlightedOption && highlightedOption.value === option.value,
    })

    return (
      <div
        data-qa-type='dropdown-item'
        data-qa-marker={ option.value }
        key={ idx }
        className={ className }
        onClick={ () => onSelect(option) }
        onMouseOver={ () => this.highlightItemByMouse(option) }
        onMouseOut={ () => this.highlightItemByMouse(null) }
      >
        { optionRendered ? optionRendered : option.label }
      </div>
    )
  }

  componentDidMount () {
    if (!this.props.isAlwaysBelowInput) {
      this.scheduleAdjustToViewport()
      window.addEventListener('scroll', this.adjustToViewportDebounced)
      window.addEventListener('resize', this.adjustToViewportDebounced)
    }
  }

  componentDidUpdate (prevProps: DropdownItemsProps<TValue>) {
    if (!this.props.isAlwaysBelowInput) {
      const isAppearing = !prevProps.isShown && this.props.isShown
      const needReposition = prevProps.options !== this.props.options || isAppearing

      if (needReposition) {
        this.scheduleAdjustToViewport()
      }
    }
  }

  componentWillUnmount () {
    if (!this.props.isAlwaysBelowInput) {
      this.adjustToViewportDebounced.cancel()
      window.removeEventListener('scroll', this.adjustToViewportDebounced)
      window.removeEventListener('resize', this.adjustToViewportDebounced)
    }
  }

  public handleArrow = (direction: 'up' | 'down') => {
    const { options } = this.props
    const { highlightedOption } = this.state

    const maxIndex = options.length - 1
    const nextIndex = highlightedOption ?
      (() => {
        const diffIndex = direction === 'up' ? -1 : 1
        const idx = options.indexOf(highlightedOption) + diffIndex
        return clamp(idx, 0, maxIndex)
      })() :
      (
        direction === 'up' ? maxIndex : 0
      )

    this.highlightItemByKey(options[nextIndex])
  }

  public handleEnter = () => {
    const { options, onSelect } = this.props
    const { highlightedOption } = this.state

    if (!highlightedOption) {
      this.highlightItemByKey(options[0])
      return
    }

    onSelect(highlightedOption)
  }

  private scheduleAdjustToViewport = () => {
    if (!this.dropdownNode) {
      return
    }

    scheduleCallOnTransitionEnd(this.dropdownNode, DROPDOWN_ITEMS_ANIMATION_TIMEOUT).then(this.adjustToViewport)
  }

  private adjustToViewport = () => {
    if (!this.props.isShown || !this.dropdownNode) {
      return
    }

    const { isAboveInput } = this.state
    const dropdownHeight = this.dropdownNode.getBoundingClientRect().height
    const dropdownParent = this.dropdownNode.parentElement as HTMLElement
    const parentBox = dropdownParent.getBoundingClientRect()
    const isFitBelow = (parentBox.bottom + dropdownHeight) <= window.innerHeight
    const isFitAbove = (parentBox.top - dropdownHeight) >= 0
    const shouldBeAbove = !isFitBelow && isFitAbove

    const shouldBeChanged = isAboveInput !== shouldBeAbove

    if (shouldBeChanged) {
      this.setState({ isAboveInput: shouldBeAbove })
    }
  }

  private adjustToViewportDebounced = debounce(this.adjustToViewport, 100)

  private adjustScroll = () => {
    if (!this.dropdownNode || !this.scrollableNode) {
      return
    }

    const selector = getClassSelector(styles.itemHighlighted)
    const item = this.dropdownNode.querySelector<HTMLDivElement>(selector)
    if (!item) {
      return
    }

    const itemTop = item.offsetTop
    const itemBottom = itemTop + item.offsetHeight
    const scrollTop = this.scrollableNode.getScrollPosition()
    const scrollBottom = scrollTop + this.scrollableNode.getScrollHeight()

    let diff = 0
    if (itemBottom > scrollBottom) {
      diff = itemBottom - scrollBottom
    }
    if (itemTop < scrollTop) {
      diff = itemTop - scrollTop
    }
    if (diff !== 0) {
      // also prevent mouse events caused by this scroll
      this.isScrollAdjustingCounter += 1
      this.scrollableNode.updateScrollPosition(scrollTop + diff)
      setTimeout(() => {
        this.isScrollAdjustingCounter -= 1
      }, 100)
    }
  }

  private highlightItemByKey = (highlightedOption: Option<TValue>) => {
    this.setState({ highlightedOption }, this.adjustScroll)
  }

  private highlightItemByMouse = (highlightedOption: Option<TValue> | null) => {
    if (this.isScrollAdjustingCounter === 0 && highlightedOption !== this.state.highlightedOption) {
      this.setState({ highlightedOption })
    }
  }

  // mousedown causes blur that prevents click event
  private handleMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault()
  }

  private handleSearch = (phrase: string) => {
    const { options } = this.props
    const found = options.find((item) => item.label.toLowerCase().startsWith(phrase))
    if (found) {
      this.highlightItemByKey(found)
    }
  }
}

export default DropdownItems
